/**
 * Request-origin context.
 *
 * Produces the value sent in the `X-Origin-Page` header on every outbound API
 * request so the backend can tell which page (and, later, which modal) a
 * request came from.
 *
 * PHASE 1 (current): page-only. The value is the current route path, e.g.
 *   X-Origin-Page: /admin/operations-dashboard
 *
 * PHASE 2 (later): modal naming. The modal stack below is already wired into
 * `buildXOrigin`, so when modals start calling `registerModal` / `unregisterModal`
 * on open/close the value automatically becomes:
 *   X-Origin-Page: /admin/operations-dashboard|ColumnSettings
 * No change to the interceptor or call sites is needed to enable it.
 */

// Separator between the page segment and the modal segment of the header value.
export const ORIGIN_SEPARATOR = "|";

// Stack of currently-open modal names. Stays empty in phase 1 (nothing
// registers yet). Using a stack means nested/stacked modals resolve to the
// most-recently-opened one (top of stack).
const modalStack = [];

/**
 * Make a value safe to send as an HTTP header: drop control characters (codes
 * 0x00-0x1F and 0x7F, which include CR/LF), collapse whitespace, and trim.
 * This guarantees a stray modal title can never break a request.
 */
const sanitize = (value) => {
  if (value == null) return "";
  const str = String(value);
  let out = "";
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) continue; // skip control chars
    out += str[i];
  }
  return out.replace(/\s+/g, " ").trim();
};

/**
 * Register a modal as open. Call on modal open/mount (phase 2).
 * Empty / blank names are ignored so an unnamed modal never appears.
 */
export const registerModal = (name) => {
  const clean = sanitize(name);
  if (!clean) return;
  modalStack.push(clean);
};

/**
 * Unregister a modal. Call on modal close/unmount (phase 2). Removes the most
 * recent matching entry so unmount order doesn't strand a stale name.
 */
export const unregisterModal = (name) => {
  const clean = sanitize(name);
  if (!clean) return;
  const idx = modalStack.lastIndexOf(clean);
  if (idx !== -1) modalStack.splice(idx, 1);
};

/** The modal currently considered "active" (top of stack), or null. */
export const getCurrentModal = () => {
  return modalStack.length ? modalStack[modalStack.length - 1] : null;
};

/**
 * Current page route. Pathname only — query string and hash are intentionally
 * excluded so analytics groups cleanly by page. Re-read on every request, so
 * route changes and browser refreshes are always reflected.
 */
export const getPageOrigin = () => {
  if (typeof window === "undefined" || !window.location) return "";
  return sanitize(window.location.pathname);
};

/**
 * Best-effort fallback: read the title of a modal that is visibly open in the
 * DOM but did not register itself via the hook (e.g. reactstrap modals that use
 * `<ModalHeader>`, which renders `.modal-title`). Returns "" when nothing is
 * found, so the value simply stays page-only — never a fake/generic name.
 */
/**
 * Primary source: read the name stamped on the currently-open modal's DOM
 * (`data-x-origin`, set by RightModal / DraggableModal / <ModalOrigin>). Because
 * a modal's DOM is inserted before its on-open fetch fires, this is present in
 * time for every modal — no lifecycle timing race. The last matching element in
 * document order is the most-recently-opened (top-most) modal.
 */
const readModalFromDom = () => {
  if (typeof document === "undefined" || !document.querySelectorAll) return "";
  const els = document.querySelectorAll("[data-x-origin]");
  for (let i = els.length - 1; i >= 0; i--) {
    const v = sanitize(els[i].getAttribute("data-x-origin"));
    if (v) return v;
  }
  return "";
};

const readModalTitleFromDom = () => {
  if (typeof document === "undefined" || !document.querySelector) return "";
  const el = document.querySelector(".modal.show .modal-title");
  return el ? sanitize(el.textContent) : "";
};

/**
 * Build the full `X-Origin-Page` header value: `page` or `page|modal`.
 *
 * Modal name priority:
 *   1. A modal registered via the hook (RightModal / DraggableModal).
 *   2. A visible reactstrap modal title read from the DOM.
 *   3. None -> page-only.
 *
 * Always returns a usable value (a missing modal never blocks the page part,
 * and a missing page never blocks a modal-only value).
 */
export const buildXOrigin = () => {
  const page = getPageOrigin();
  // DOM marker (primary) -> JS stack (fallback) -> reactstrap title (last resort).
  const modal = readModalFromDom() || getCurrentModal() || readModalTitleFromDom();
  if (page && modal) return `${page}${ORIGIN_SEPARATOR}${modal}`;
  return page || modal || "";
};
